import { Delete as DeleteIcon, Edit as EditIcon } from "@mui/icons-material";
import { Chip } from "@mui/material";
import React from "react";
import { format } from "date-fns";
import { Tenant } from "../../../api/tenantsApi";
import { DataTable } from "../../../components/common/DataTable/DataTable";
import { getActiveStatusColor } from "../../../utils/statusColors";

interface TenantsListProps {
  tenants: Tenant[];
  isLoading: boolean;
  onEditClick: (tenant: Tenant) => void;
  onDeleteClick: (tenant: Tenant) => void;
  totalCount?: number;
  hasNextPage?: boolean;
  isLoadingMore?: boolean;
  onLoadMore?: () => void;
}

const formatDate = (value: string | null | undefined, emptyLabel: string) => {
  if (!value) return <span style={{ color: "#9e9e9e" }}>{emptyLabel}</span>;
  try {
    return format(new Date(value), "MMM dd, yyyy");
  } catch {
    return value;
  }
};

const subscriptionStatusColor = (status?: string | null) => {
  switch ((status || "").toLowerCase()) {
    case "trialing":
      return "info";
    case "active":
      return "success";
    case "past_due":
      return "warning";
    case "canceled":
      return "default";
    default:
      return "default";
  }
};

/** Plan + billing state in one label, e.g. "Pro · Trial" or "Lite · Paid". */
const formatPlanSubscription = (tenant: Tenant) => {
  if (!tenant.subscriptionStatus && !tenant.planName && !tenant.planCode) {
    return "No subscription";
  }

  const plan = tenant.planName || tenant.planCode || "Unknown plan";
  const status = (tenant.subscriptionStatus || "").toLowerCase();

  if (tenant.isTrialing || status === "trialing") {
    return `${plan} · Trial`;
  }
  if (status === "active") {
    return `${plan} · Paid`;
  }
  if (status === "past_due") {
    return `${plan} · Past due`;
  }
  if (status === "canceled") {
    return `${plan} · Canceled`;
  }
  if (status) {
    return `${plan} · ${status.replace(/_/g, " ")}`;
  }
  return plan;
};

const TenantsList: React.FC<TenantsListProps> = ({
  tenants,
  isLoading,
  onEditClick,
  onDeleteClick,
  totalCount,
  hasNextPage,
  isLoadingMore,
  onLoadMore,
}) => {
  const columns = [
    { field: "name", headerName: "Company Name", flex: 1.4 },
    { field: "contactEmail", headerName: "Contact Email", flex: 1.3 },
    {
      field: "isActive",
      headerName: "Tenant",
      flex: 0.7,
      renderCell: (value: boolean) => (
        <span
          style={{
            color: getActiveStatusColor(value),
            fontWeight: "bold",
          }}
        >
          {value ? "Active" : "Inactive"}
        </span>
      ),
    },
    {
      field: "planName",
      headerName: "Subscription",
      flex: 1.4,
      renderCell: (_: string | null | undefined, row: Tenant) => (
        <Chip
          label={formatPlanSubscription(row)}
          size="small"
          color={subscriptionStatusColor(row.subscriptionStatus) as any}
          variant="outlined"
        />
      ),
    },
    {
      field: "subscriptionStartDate",
      headerName: "Starts",
      flex: 0.9,
      renderCell: (value: string | null | undefined) =>
        formatDate(value, "Not set"),
    },
    {
      field: "currentPeriodEnd",
      headerName: "Period / Trial End",
      flex: 1,
      renderCell: (_: string | null | undefined, row: Tenant) => {
        if (row.isTrialing && row.trialEndDate) {
          return formatDate(row.trialEndDate, "Not set");
        }
        if (row.subscriptionEndDate) {
          return formatDate(row.subscriptionEndDate, "Not set");
        }
        return formatDate(row.currentPeriodEnd, "Not set");
      },
    },
  ];

  const getActions = () => [
    {
      icon: <EditIcon />,
      label: "Edit",
      onClick: onEditClick,
      color: "info" as const,
    },
    {
      icon: <DeleteIcon />,
      label: "Delete",
      onClick: onDeleteClick,
      color: "error" as const,
    },
  ];

  return (
    <DataTable<Tenant>
      items={tenants}
      columns={columns}
      actions={getActions}
      isLoading={isLoading}
      title="Tenants"
      totalCount={totalCount}
      hasNextPage={hasNextPage}
      isLoadingMore={isLoadingMore}
      onLoadMore={onLoadMore}
    />
  );
};

export default TenantsList;
